/**
 * Modal "Configurar Etiqueta" para a lista de matérias-primas.
 * Permite definir largura e altura da etiqueta em cm; converte para px (DPI 203).
 *
 * Dependências opcionais para preview avançado (canvas/konva):
 *   npm i konva react-konva
 *   npm i -D @types/konva   # se o TypeScript reclamar de tipos
 *   yarn: yarn add konva react-konva && yarn add -D @types/konva
 * Este componente usa apenas div para preview; konva fica documentado para uso futuro.
 */

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import { useEffect, useState } from 'react'

// MUI Dialog exige ThemeProvider (usa theme.transitions.duration); app usa Chakra, então envolvemos só o modal
const muiTheme = createTheme()
import {
  cmToPx,
  DEFAULT_DPI,
  getDefaultLabelSize,
  type LabelSize,
} from '../../utils/labelSize'

const STORAGE_KEY = 'label_size_cm'
const CM_MIN = 1
const CM_MAX = 30
const CM_STEP = 0.1

export interface MateriaPrimaLabelModalProps {
  open: boolean
  onClose: () => void
  onApply: (size: LabelSize) => void
}

function loadSavedSize(): { widthCm: number; heightCm: number } | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { widthCm: number; heightCm: number }
    if (
      typeof parsed?.widthCm === 'number' &&
      typeof parsed?.heightCm === 'number' &&
      parsed.widthCm >= CM_MIN &&
      parsed.widthCm <= CM_MAX &&
      parsed.heightCm >= CM_MIN &&
      parsed.heightCm <= CM_MAX
    ) {
      return { widthCm: parsed.widthCm, heightCm: parsed.heightCm }
    }
  } catch {
    // ignore
  }
  return null
}

export function MateriaPrimaLabelModal({ open, onClose, onApply }: MateriaPrimaLabelModalProps) {
  const defaultSize = getDefaultLabelSize()
  const [widthCm, setWidthCm] = useState(defaultSize.widthCm)
  const [heightCm, setHeightCm] = useState(defaultSize.heightCm)

  // Ao abrir, preencher com valor salvo (localStorage) ou default
  useEffect(() => {
    if (open) {
      const saved = loadSavedSize()
      if (saved) {
        setWidthCm(saved.widthCm)
        setHeightCm(saved.heightCm)
      } else {
        setWidthCm(defaultSize.widthCm)
        setHeightCm(defaultSize.heightCm)
      }
    }
  }, [open, defaultSize.widthCm, defaultSize.heightCm])

  const widthPx = cmToPx(widthCm)
  const heightPx = cmToPx(heightCm)

  const handleCancel = () => {
    setWidthCm(defaultSize.widthCm)
    setHeightCm(defaultSize.heightCm)
    onClose()
  }

  const handleApply = () => {
    const size: LabelSize = {
      widthPx,
      heightPx,
      widthCm,
      heightCm,
    }
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ widthCm, heightCm }))
    } catch {
      // persistência opcional
    }
    onApply(size)
    onClose()
  }

  const clamp = (v: number) => Math.max(CM_MIN, Math.min(CM_MAX, v))

  return (
    <ThemeProvider theme={muiTheme}>
      <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Configurar Etiqueta</DialogTitle>
        <DialogContent>
          <TextField
            label="Largura (cm)"
            type="number"
            value={widthCm}
            onChange={(e) => setWidthCm(clamp(Number(e.target.value) || CM_MIN))}
            inputProps={{ min: CM_MIN, max: CM_MAX, step: CM_STEP }}
            fullWidth
            margin="normal"
            sx={{ mb: 2 }}
          />
          <TextField
            label="Altura (cm)"
            type="number"
            value={heightCm}
            onChange={(e) => setHeightCm(clamp(Number(e.target.value) || CM_MIN))}
            inputProps={{ min: CM_MIN, max: CM_MAX, step: CM_STEP }}
            fullWidth
            margin="normal"
            sx={{ mb: 2 }}
          />
          {/* Preview simples do retângulo da etiqueta (proporção em px, tamanho limitado para caber no modal) */}
          <div style={{ marginTop: 16, marginBottom: 8 }}>
            <div
              style={{
                border: '2px solid #666',
                backgroundColor: '#f5f5f5',
                width: '100%',
                maxWidth: 200,
                aspectRatio: `${widthPx} / ${heightPx}`,
              }}
              aria-hidden
            />
          </div>
          <p style={{ margin: 0, fontSize: 12, color: '#666' }}>
            Tamanho interno: {widthPx}px × {heightPx}px (DPI {DEFAULT_DPI})
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>Cancelar</Button>
          <Button onClick={handleApply} variant="contained" color="primary">
            Aplicar
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  )
}
